import '.f{position:fixed;}.ssr-plugin'
import '.ck{z-index:20;}.ssr-plugin'
import '.aw{position:relative;}.ssr-plugin'
import '.c6{display:inline-flex;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.a1{cursor:pointer;}.ssr-plugin'
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEventHandler,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { styled, type Style } from 'inlines'
import Column from '@app/components/layout/Column'
import { HorizontalSeparator } from '@app/components/core/separator/HorizontalSeparator'
import {
  TextLink,
  type TextLinkProps,
} from '@app/components/core/link/TextLink'
import useMediaQuery from '@app/util/useMediaQuery'

export interface DropdownMenuItem {
  label: string
  to?: string
  id?: string
  linkProps?: Omit<TextLinkProps, 'children'>
}

type DropdownMenuAlignment = 'start' | 'center' | 'end'

interface DropdownMenuProps {
  items: DropdownMenuItem[]
  children: ReactNode
  showOnHoverDesktop?: boolean
  alignment?: DropdownMenuAlignment
  open?: boolean
}

const baseItemStyle: Style = {
  padding: '1rem 1.5rem',
  justifyContent: 'flex-start',
  width: '100%',
  textDecoration: 'none',

  '&:hover': {
    backgroundColor: 'var(--gray-100)',
  },
}

const defaultTextStyle: Style = {
  fontWeight: 400,
}

const MENU_GAP = '1rem'

type MenuPosition = {
  top: number
  left?: number
  right?: number
  transform?: string
}

export const DropdownMenu = ({
  items,
  children,
  showOnHoverDesktop = false,
  open: externalOpen,
  alignment = 'end',
}: DropdownMenuProps) => {
  const isMobile = useMediaQuery()
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const open = externalOpen ?? isOpen
  const shouldRenderMenu = open && items.length > 0
  const useHover = showOnHoverDesktop && !isMobile
  const horizontalAlignment = isMobile ? 'end' : alignment

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  // Hover menus span a transparent gap between the trigger and the menu; a
  // short close delay lets the pointer cross it without the menu closing.
  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setIsOpen(false), 120)
  }

  useEffect(() => {
    return () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current)
        closeTimer.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return
      }

      setIsOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    // The menu is positioned once from the trigger's rect, so close it on
    // scroll rather than tracking the trigger as the page moves.
    const handleScroll = () => setIsOpen(false)

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('scroll', handleScroll, true)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen, setIsOpen])

  // Position the portalled menu from the trigger's viewport rect (computed once
  // when it opens; it closes on scroll, so it never needs to follow the page).
  useLayoutEffect(() => {
    if (!shouldRenderMenu || typeof window === 'undefined') {
      return
    }

    const trigger = triggerRef.current
    if (!trigger) {
      return
    }

    const rect = trigger.getBoundingClientRect()

    if (horizontalAlignment === 'start') {
      setMenuPosition({ top: rect.bottom, left: rect.left })
    } else if (horizontalAlignment === 'center') {
      setMenuPosition({
        top: rect.bottom,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      })
    } else {
      setMenuPosition({
        top: rect.bottom,
        right: window.innerWidth - rect.right,
      })
    }
  }, [shouldRenderMenu, horizontalAlignment])

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setIsOpen((previous) => !previous)
    }
  }

  const triggerClickHandler: MouseEventHandler<HTMLDivElement> | undefined =
    useHover
      ? undefined
      : (event) => {
          event.preventDefault()
          event.stopPropagation()
          setIsOpen((previous) => !previous)
        }

  const menu =
    shouldRenderMenu && menuPosition && typeof window !== 'undefined'
      ? createPortal(
          <div className="f ck"
            ref={menuRef}
            onMouseEnter={
              useHover
                ? () => {
                    cancelClose()
                    setIsOpen(true)
                  }
                : undefined
            }
            onMouseLeave={useHover ? scheduleClose : undefined}
            onClick={(event) => {
              event.stopPropagation()
            }}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              right: menuPosition.right,
              transform: menuPosition.transform,
              paddingTop: MENU_GAP,}}
          >
            <Column
              gap={0}
              onClick={() => setIsOpen(false)}
              style={{
                borderRadius: '0.625rem',
                backgroundColor: 'var(--palette-white)',
                border: '1px solid var(--gray-100)',
                boxShadow: '0 3px 10px 0 rgba(0, 0, 0, 0.05)',
                alignItems: 'stretch',
                overflow: 'hidden',
              }}
            >
              {items.map((item, index) => {
                const { containerStyle, textStyle, to, onClick } =
                  item.linkProps ?? {}

                return (
                  <Fragment key={item.id ?? `${item.id}-${index}`}>
                    <TextLink
                      to={to}
                      onClick={onClick}
                      containerStyle={{ ...baseItemStyle, ...containerStyle }}
                      textStyle={{ ...defaultTextStyle, ...textStyle }}
                    >
                      {item.label}
                    </TextLink>

                    {index < items.length - 1 && (
                      <HorizontalSeparator color="var(--gray-100)" />
                    )}
                  </Fragment>
                )
              })}
            </Column>
          </div>,
          document.body,
        )
      : null

  return (
    <div className="aw c6 t"
      onMouseEnter={
        useHover
          ? () => {
              cancelClose()
              setIsOpen(true)
            }
          : undefined
      }
      onMouseLeave={useHover ? scheduleClose : undefined}
    >
      <div className="a1 c6"
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={shouldRenderMenu}
        onClick={triggerClickHandler}
        onKeyDown={handleTriggerKeyDown}
      >
        {children}
      </div>
      {menu}
    </div>
  )
}
